/**
 * Engagement Message Context - User messaging system
 *
 * Manages state for admin-controlled modal messages:
 * - Fetches pending messages on auth
 * - Records impressions and dismissals
 * - A message opens at most once per show, including a duplicate copy
 * - App-provisioned writers never see connect-your-key onboarding
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useUserContext } from './UserContext';
import { api } from '../api/client';
import * as messagesApi from '../api/messages';
import {
  appProvisionedWriter,
  engagementContentKey,
  isConnectKeyOnboarding,
  isExplainerMessage,
  shouldPresentEngagementMessage,
} from '../utils/engagementShow';

const EngagementMessageContext = createContext(null);

const SHOWN_KEY = 'shownEngagementMessages';

function readShownIds() {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(SHOWN_KEY) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeShownId(messageId) {
  const shown = readShownIds();
  if (!shown.includes(messageId)) {
    sessionStorage.setItem(SHOWN_KEY, JSON.stringify([...shown, messageId]));
  }
}

async function loadWriterStatus() {
  try {
    const balance = await api.get('/me/openrouter/balance');
    return balance?.data || null;
  } catch {
    return null;
  }
}

export function EngagementMessageProvider({ children }) {
  const { userId, isLoaded, isSignedIn } = useUserContext();
  const [pendingMessage, setPendingMessage] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [dismissQueue, setDismissQueue] = useState([]);
  const [isProcessingDismiss, setIsProcessingDismiss] = useState(false);
  const [shownUrgentIds, setShownUrgentIds] = useState(new Set());
  const presentedIdsRef = useRef(new Set());
  const presentedContentRef = useRef(new Set());
  const visibleRef = useRef(false);
  const writerStatusRef = useRef(null);
  const pendingRef = useRef(null);
  const openGenerationRef = useRef(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const explainerId = searchParams.get('explainer');
  const explainerLinkRef = useRef(explainerId);
  explainerLinkRef.current = explainerId;

  useEffect(() => {
    visibleRef.current = isVisible;
  }, [isVisible]);

  useEffect(() => {
    pendingRef.current = pendingMessage;
  }, [pendingMessage]);

  const presentState = useCallback((writerStatus) => ({
    presentedIds: presentedIdsRef.current,
    presentedContentKeys: presentedContentRef.current,
    sessionShownIds: readShownIds(),
    writerStatus: writerStatus ?? writerStatusRef.current,
  }), []);

  const rememberShow = useCallback((message) => {
    if (!message?.id) return;
    presentedIdsRef.current.add(message.id);
    presentedContentRef.current.add(engagementContentKey(message));
    writeShownId(message.id);
  }, []);

  const openMessage = useCallback(async (message) => {
    rememberShow(message);
    setPendingMessage(message);
    setIsVisible(true);
    setDontShowAgain(false);
    try {
      await messagesApi.recordImpression(message.id);
    } catch (err) {
      console.error('Failed to record impression:', err);
    }
  }, [rememberShow]);

  const clearExplainerParam = useCallback(() => {
    setSearchParams((prev) => {
      if (!prev.get('explainer')) return prev;
      const next = new URLSearchParams(prev);
      next.delete('explainer');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const openOnDemand = useCallback(async (messageId) => {
    if (!messageId) return;
    const current = pendingRef.current;
    if (visibleRef.current && current?.id === messageId && isExplainerMessage(current)) {
      return;
    }
    const generation = ++openGenerationRef.current;
    try {
      const result = await messagesApi.getOnDemand(messageId);
      if (generation !== openGenerationRef.current) return;
      const message = result?.data;
      if (!message || !isExplainerMessage(message)) return;
      setPendingMessage(message);
      setIsVisible(true);
      setDontShowAgain(false);
      try {
        await messagesApi.recordImpression(message.id);
      } catch (err) {
        console.error('Failed to record impression:', err);
      }
    } catch (err) {
      if (generation !== openGenerationRef.current) return;
      console.error('Failed to open explainer:', err);
    }
  }, []);

  const checkForMessages = useCallback(async () => {
    if (!userId || !isSignedIn || explainerLinkRef.current) return;

    try {
      const [result, writerStatus] = await Promise.all([
        messagesApi.getPending(),
        loadWriterStatus(),
      ]);
      if (writerStatus) writerStatusRef.current = writerStatus;
      const message = result?.data;
      if (explainerLinkRef.current) return;
      if (!message || visibleRef.current) return;
      if (isExplainerMessage(message)) return;

      // Never open connect-your-key onboarding for a key the app provisioned.
      // Close the row so a duplicate copy cannot open on the next sign-in.
      if (appProvisionedWriter(writerStatus) && isConnectKeyOnboarding(message)) {
        if (!presentedIdsRef.current.has(message.id)) {
          rememberShow(message);
          setDismissQueue((queue) => [
            ...queue,
            { messageId: message.id, dontShow: true, action: 'dismiss' },
          ]);
        }
        return;
      }

      if (!shouldPresentEngagementMessage(message, presentState(writerStatus))) return;
      await openMessage(message);
    } catch (error) {
      // Silently fail - messages are non-critical
      console.error('Error checking for engagement messages:', error);
    }
  }, [userId, isSignedIn, openMessage, presentState, rememberShow]);

  // Check on initial load after auth
  useEffect(() => {
    if (isLoaded && isSignedIn && userId) {
      checkForMessages();
    }
  }, [isLoaded, isSignedIn, userId, checkForMessages]);

  // Poll for urgent messages every 30 seconds
  const checkForUrgentMessages = useCallback(async () => {
    if (!userId || !isSignedIn || visibleRef.current || explainerLinkRef.current) return;

    try {
      const result = await messagesApi.getUrgent();
      const urgentMessages = result.data || [];
      const unshown = urgentMessages.find((msg) => (
        !isExplainerMessage(msg)
        && !shownUrgentIds.has(msg.id)
        && shouldPresentEngagementMessage(msg, presentState())
      ));
      if (unshown) {
        setShownUrgentIds((prev) => new Set([...prev, unshown.id]));
        await openMessage(unshown);
      }
    } catch (error) {
      console.error('Error checking for urgent messages:', error);
    }
  }, [userId, isSignedIn, shownUrgentIds, openMessage, presentState]);

  useEffect(() => {
    if (!isLoaded || !isSignedIn || !userId) return;
    const intervalId = setInterval(checkForUrgentMessages, 30000);
    return () => clearInterval(intervalId);
  }, [isLoaded, isSignedIn, userId, checkForUrgentMessages]);

  // Process dismiss queue to handle rapid dismissals
  useEffect(() => {
    const processDismissQueue = async () => {
      if (isProcessingDismiss || dismissQueue.length === 0) return;

      setIsProcessingDismiss(true);
      const { messageId, dontShow, action } = dismissQueue[0];
      let followUp = null;

      try {
        writeShownId(messageId);
        const result = await messagesApi.dismiss(messageId, dontShow, action);
        const next = result?.data?.next_message;

        if (isExplainerMessage(next)) {
          setPendingMessage(null);
          setIsVisible(false);
        } else if (next && shouldPresentEngagementMessage(next, presentState())) {
          await openMessage(next);
        } else if (next?.id && !presentedIdsRef.current.has(next.id)) {
          // Same show, another published copy. Close it without opening again.
          rememberShow(next);
          followUp = { messageId: next.id, dontShow, action: 'dismiss' };
          setPendingMessage(null);
          setIsVisible(false);
        } else {
          setPendingMessage(null);
          setIsVisible(false);
        }
      } catch (error) {
        console.error('Error dismissing message:', error);
        setPendingMessage(null);
        setIsVisible(false);
      } finally {
        setDismissQueue((queue) => {
          const rest = queue.slice(1);
          return followUp ? [...rest, followUp] : rest;
        });
        setIsProcessingDismiss(false);
      }
    };

    processDismissQueue();
  }, [dismissQueue, isProcessingDismiss, openMessage, presentState, rememberShow]);

  /**
   * Dismiss the current message.
   * Hides modal immediately, queues API call.
   */
  const dismissMessage = useCallback((messageId, dontShow, action) => {
    const current = pendingRef.current;
    if (current && current.id === messageId && isExplainerMessage(current)) {
      openGenerationRef.current += 1;
      setPendingMessage(null);
      setIsVisible(false);
      setDontShowAgain(false);
      clearExplainerParam();
      return;
    }
    if (current && current.id === messageId) {
      rememberShow(current);
    } else if (messageId) {
      presentedIdsRef.current.add(messageId);
      writeShownId(messageId);
    }
    setIsVisible(false);
    setDismissQueue((queue) => [...queue, { messageId, dontShow, action }]);
  }, [rememberShow, clearExplainerParam]);

  useEffect(() => {
    if (!explainerId || !isLoaded || !isSignedIn || !userId) return;
    openOnDemand(explainerId);
  }, [explainerId, isLoaded, isSignedIn, userId, openOnDemand]);

  const value = {
    pendingMessage,
    isVisible,
    dontShowAgain,
    setDontShowAgain,
    dismissMessage,
    checkForMessages,
    openOnDemand,
  };

  return (
    <EngagementMessageContext.Provider value={value}>
      {children}
    </EngagementMessageContext.Provider>
  );
}

export function useEngagementMessage() {
  const context = useContext(EngagementMessageContext);
  if (!context) {
    throw new Error('useEngagementMessage must be used within EngagementMessageProvider');
  }
  return context;
}

export default EngagementMessageContext;
